import React from "react";
import PropTypes from "prop-types";
import ContentGrid from "arena/components/ContentGrid";
import ContentCarousel from "arena/components/ContentCarousel";
import ContentList from "arena/components/ContentList";
import HomepageTitle from "arena/components/HomepageTitle";
import HeaderMoreLink from "arena/components/HeaderMoreLink";
import Box from "@mui/material/Box";
import Link from "arena/components/Link";
import { useQuery } from "@apollo/client";
import { ArenaContentsQuery } from "arena/queries";
import ReactGA from "react-ga4";
import { useArena } from "core/components/ArenaProvider";

const components = {
  CAROUSEL: ContentCarousel,
  GRID: ContentGrid,
  LIST: ContentList,
};

const STREAM = "STREAM";

export default function ArenaContents({
  // blockId,
  search,
  title,
  view = "CAROUSEL",
  skip,
  sort = "LATEST",
  period,
  limit = 12,
  type,
  status,
  xs = 6,
  sm = 6,
  md = 4,
  lg,
  xl,
  noMore,
  onCompleted,
  loadMore,
  inView,
  itemsPerRow,

  relatedContentId,
  selectedContentId,
  rightholderIds,
  productId, // пропс из ContentPlaylist
  productIds, // пропс из компонента арены
  collectionId, // пропс из страницы коллекции
  collectionIds, // пропс из компонента арены
  tagIds,
  anyTagIds,
}) {
  const arena = useArena();

  itemsPerRow = itemsPerRow || arena.defaultRowSize;

  const variables = {
    search,
    sort,
    type,
    relatedContentId,
    productId,
    productIds,
    tagIds,
    anyTagIds,
  };

  if (skip) {
    variables.skip = skip;
  }

  if (limit) {
    variables.limit = limit;
  }

  if (period) {
    variables.period = period;
  }

  if (type === STREAM) {
    variables.status = status;
  }

  if (rightholderIds?.length) {
    variables.rightholderIds = rightholderIds;
  }

  /**
   * Если это полка с фильтром по одной коллекции
   * выставим collectionId что бы контенты открывались с плейлистом коллекции
   */
  if (collectionIds?.length === 1) {
    collectionId = collectionIds[0];
  }

  if (collectionId || collectionIds?.length) {
    variables.collectionIds = collectionId ? [collectionId] : collectionIds;
  }

  const { data, loading, fetchMore, called } = useQuery(ArenaContentsQuery, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first", // После первого запроса используем кеш
    notifyOnNetworkStatusChange: false, // Не вызывать перерендер при network-only запросах
    variables,
    onCompleted,
    skip: !inView,
  });

  const Component = components[view];

  let moreLink = null;

  // Проверяем случаи с одним тегом или одной коллекцией - для них moreLink всегда
  const hasOneCollection = collectionIds?.length === 1;
  const hasOneTag = tagIds?.length === 1;
  const hasOneAnyTag = anyTagIds?.length === 1;
  const shouldAlwaysShowMoreLink =
    hasOneCollection || hasOneTag || hasOneAnyTag;

  if (!noMore) {
    if (
      shouldAlwaysShowMoreLink ||
      (["LATEST", "OLDEST"].includes(sort) && data?.contents?.count > limit)
    ) {
      const searchParams = new URLSearchParams();

      if (status) searchParams.set("status", status);
      if (period) searchParams.set("period", period);
      if (type) searchParams.set("type", type);
      if (sort) searchParams.set("sort", sort);
      if (title) searchParams.set("blockTitle", title);

      if (productIds) {
        productIds.forEach((productId) => {
          searchParams.append("productId", productId);
        });
      }

      if (tagIds) {
        tagIds.forEach((tagId) => {
          searchParams.append("tagId", tagId);
        });
      }

      if (anyTagIds) {
        anyTagIds.forEach((tagId) => {
          searchParams.append("anyTagIds", tagId);
        });
      }

      if (rightholderIds) {
        rightholderIds.forEach((rightholderId) => {
          searchParams.append("rightholderId", rightholderId);
        });
      }

      if (collectionIds) {
        collectionIds.forEach((collectionId) => {
          searchParams.append("collectionId", collectionId);
        });
      }

      moreLink = `/contents?${searchParams.toString()}`;

      /**
       * Если полка одной коллекции
       */
      if (hasOneCollection) {
        moreLink = `/collection/${collectionIds[0]}`;
      }

      /**
       * Если полка одного тега
       */
      if (hasOneTag) {
        moreLink = `/tag/${tagIds[0]}`;
      }

      /**
       * Если полка одного anyTag
       */
      if (hasOneAnyTag) {
        moreLink = `/tag/${anyTagIds[0]}`;
      }
    }
  }

  const count = data?.contents?.count || 0;
  const nodes = data?.contents?.nodes || [];

  if (nodes.length === 0) {
    if (called) {
      if (!loading) {
        return null;
      }
    }

    // пустой блок, чтобы работала загрузка по доскролу
    return <div style={{ height: "20vh", width: "100%" }} />;
  }

  const hasMore = nodes.length < count;
  const nextPage = Math.ceil(nodes.length / limit);

  const onLoadMore = () => {
    // Предотвращаем повторные вызовы во время загрузки
    if (loading) {
      return;
    }

    ReactGA.event({
      category: "ArenaContents",
      action: "Fetch more contents",
      label: nextPage + " page",
    });

    fetchMore({
      variables: {
        skip: nextPage * limit,
      },
    });
  };

  if (itemsPerRow) {
    sm = Math.max(12 / itemsPerRow, 4);
    md = Math.max(12 / itemsPerRow, 3);
    lg = 12 / itemsPerRow;
    xl = lg;
  }

  // Создаем заголовок как ссылку когда есть moreLink
  let titleElement = title;
  if (moreLink && typeof title === "string") {
    titleElement = (
      <Link to={moreLink} style={{ textDecoration: "none", color: "inherit" }}>
        {title}
      </Link>
    );
  }

  return (
    <>
      {title ? (
        <HomepageTitle
          title={titleElement}
          action={moreLink && <HeaderMoreLink url={moreLink} />}
        />
      ) : (
        <Box sx={{ mt: 2 }} />
      )}

      <Component
        nodes={nodes}
        xs={xs}
        sm={sm}
        md={md}
        lg={lg}
        xl={xl}
        productId={productId}
        collectionId={collectionId}
        contentId={selectedContentId}
        itemsPerRow={itemsPerRow}
        hasMore={loadMore && hasMore}
        onLoadMore={onLoadMore}
        loading={loading}
        nextPage={nextPage}
      />
    </>
  );
}

ArenaContents.propTypes = {
  itemsPerRow: PropTypes.oneOf([2, 4, 3, 6]),
  xs: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  search: PropTypes.string,
  title: PropTypes.node,
  view: PropTypes.string,
  tagIds: PropTypes.arrayOf(PropTypes.string),
  anyTagIds: PropTypes.arrayOf(PropTypes.string),
  productIds: PropTypes.arrayOf(PropTypes.string),
  sort: PropTypes.string,
  period: PropTypes.number,
  limit: PropTypes.number,
  skip: PropTypes.number,
  type: PropTypes.string,
  status: PropTypes.string,
  noMore: PropTypes.bool,
  onCompleted: PropTypes.func,
  relatedContentId: PropTypes.string,
  // blockId: PropTypes.string,
  productId: PropTypes.string,
  collectionId: PropTypes.string,
  selectedContentId: PropTypes.string,
  loadMore: PropTypes.bool,
  rightholderIds: PropTypes.arrayOf(PropTypes.string),
  collectionIds: PropTypes.arrayOf(PropTypes.string),
  index: PropTypes.number,
  inView: PropTypes.bool,
};
